import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import { authGet } from '../../lib/api';

function formatRecentTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

const recentActionLabels = {
  search: 'Search',
  select_student: 'Opened',
  create_student: 'Created',
  update_student: 'Edited',
  delete_student: 'Deleted',
  enroll_student: 'Enrolled',
  collect_payment: 'Payment',
  move_student: 'Moved',
  remove_student: 'Removed',
  print_exit_pdf: 'PDF Export'
};

const recentActionColors = {
  search: 'default',
  select_student: 'info',
  create_student: 'success',
  update_student: 'warning',
  delete_student: 'error',
  enroll_student: 'success',
  collect_payment: 'success',
  move_student: 'secondary',
  remove_student: 'error',
  print_exit_pdf: 'info'
};

export default function RecentsPage() {
  const [recents, setRecents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadRecents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await authGet('/api/intake-recents?limit=50');
      setRecents(data.recents || []);
    } catch (err) {
      setError(err.message || 'Unable to load recent activity');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecents();
  }, [loadRecents]);

  return (
    <Box p={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} mb={3}>
        <Box>
          <Typography variant="h3" gutterBottom>Recents</Typography>
          <Typography variant="body1" color="text.secondary">
            Previous Intake actions recorded under your logged-in user only.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={loadRecents} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ p: 3 }}>
        {recents.length === 0 ? (
          <Alert severity="info">
            No recent intake activity yet. Searches, opened students, edits, payments, enrollments, and moves will show here after you perform them.
          </Alert>
        ) : (
          <Stack spacing={1.25}>
            {recents.map((recent) => (
              <Paper key={recent.id} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Chip
                        size="small"
                        label={recentActionLabels[recent.action_type] || recent.action_type}
                        color={recentActionColors[recent.action_type] || 'default'}
                      />
                      <Typography variant="body1">{recent.label}</Typography>
                    </Stack>
                    {(recent.student_name?.trim() || recent.class_name) && (
                      <Typography variant="body2" color="text.secondary" mt={0.5}>
                        {[recent.student_name?.trim(), recent.class_name].filter(Boolean).join(' • ')}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">{formatRecentTime(recent.created_at)}</Typography>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
